import { useState, useEffect, useRef, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { useSearchParams } from "react-router-dom";
import api, { conversationApi, chatbotApi, workspaceApi, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Loader2, Search, SlidersHorizontal, MoreHorizontal, Download, Send,
  MessageSquare, Check,
} from "lucide-react";
import { toast } from "sonner";

/* Live chat and chat history were two pages looking at the same records.
   They are one inbox now: the list on the left, the conversation on the
   right, and replying is just something you can do while you are there. */

const POLL_MS = 5000;
const PAGE = 50;
const FILTER_KEY = "bicarra:conversations:filters";

const MD = {
  p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc list-inside mb-1.5 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside mb-1.5 space-y-0.5">{children}</ol>,
  code: ({ inline, children }) => inline
    ? <code className="bg-black/[0.06] px-1 rounded text-[12px] font-mono">{children}</code>
    : <code className="block bg-black/[0.06] p-2 rounded text-[12px] font-mono my-1.5 whitespace-pre-wrap">{children}</code>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="underline">{children}</a>
  ),
};

function who(conv) {
  return conv?.visitor_name || conv?.visitor_email || "Visitor";
}

function shortDate(v) {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function download(blobOrText, filename, type) {
  const blob = blobOrText instanceof Blob ? blobOrText : new Blob([blobOrText], { type });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

export default function Conversations() {
  const [tab, setTab] = useState("all");
  const [conversations, setConversations] = useState([]);
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [search, setSearch] = useState("");
  const [filterBot, setFilterBot] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState([]);

  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const [msgQuery, setMsgQuery] = useState("");
  const [msgResults, setMsgResults] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const endRef = useRef(null);
  const selectedRef = useRef(null);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  /* Remember how you last left the filters. */
  useEffect(() => {
    try {
      const v = JSON.parse(localStorage.getItem(FILTER_KEY) || "{}");
      if (typeof v.search === "string") setSearch(v.search);
      if (typeof v.filterBot === "string") setFilterBot(v.filterBot);
    } catch {}
  }, []);
  useEffect(() => {
    try { localStorage.setItem(FILTER_KEY, JSON.stringify({ search, filterBot })); } catch {}
  }, [search, filterBot]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = search.trim().length >= 2 ? search.trim() : undefined;
      const [convs, bots] = await Promise.all([
        conversationApi.list(filterBot !== "all" ? filterBot : undefined, q, undefined, PAGE, fromDate || undefined, toDate || undefined),
        chatbotApi.list().catch(() => ({ data: [] })),
      ]);
      const list = Array.isArray(convs.data) ? convs.data : [];
      setConversations(list);
      setHasMore(list.length >= PAGE);
      setChatbots(Array.isArray(bots.data) ? bots.data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [search, filterBot, fromDate, toDate]);

  useEffect(() => { load(); }, [filterBot, fromDate, toDate]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  /* Quietly keep the open conversation and the list current. No sound, no
     badge: if you are looking at it you will see it arrive. */
  useEffect(() => {
    const id = setInterval(async () => {
      if (document.hidden) return;
      try {
        const res = await conversationApi.list(filterBot !== "all" ? filterBot : undefined, undefined, undefined, PAGE);
        if (Array.isArray(res.data)) setConversations(res.data);
        const open = selectedRef.current;
        if (open) {
          const m = await conversationApi.getMessages(open.id);
          if (Array.isArray(m.data)) setMessages(m.data);
        }
      } catch { /* a dropped poll is not worth a toast */ }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [filterBot]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const open = async (conv) => {
    setSelected(conv);
    setMessages([]);
    setLoadingMessages(true);
    try {
      const res = await conversationApi.getMessages(conv.id);
      setMessages(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoadingMessages(false);
    }
  };

  /* BC-021: open the conversation named in the URL (?conv=<id>), for example
     from a chatbot's recent conversations. It is fetched directly, so it opens
     even when it is not on the first page of the list or hidden by a filter. */
  const [searchParams] = useSearchParams();
  const deepLinkRef = useRef(null);
  useEffect(() => {
    const id = searchParams.get("conv");
    if (!id || deepLinkRef.current === id) return;
    deepLinkRef.current = id;
    (async () => {
      try {
        const res = await conversationApi.get(id);
        if (res.data && res.data.id) open(res.data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    })();
  }, [searchParams]); // eslint-disable-line
  /* Once the list has it, use the list item: it carries visitor and lead details. */
  useEffect(() => {
    const cur = selectedRef.current;
    if (!cur || cur.id !== deepLinkRef.current) return;
    const fresh = conversations.find((c) => c.id === cur.id);
    if (fresh && fresh !== cur) setSelected({ ...cur, ...fresh });
  }, [conversations]);

  const loadMore = async () => {
    if (!hasMore || loadingMore || !conversations.length) return;
    setLoadingMore(true);
    try {
      const before = conversations[conversations.length - 1]?.created_at;
      const q = search.trim().length >= 2 ? search.trim() : undefined;
      const res = await conversationApi.list(
        filterBot !== "all" ? filterBot : undefined, q, before, PAGE,
        fromDate || undefined, toDate || undefined,
      );
      const more = Array.isArray(res.data) ? res.data : [];
      setConversations((p) => [...p, ...more]);
      setHasMore(more.length >= PAGE);
    } catch { toast.error("Could not load more"); }
    finally { setLoadingMore(false); }
  };

  const patch = (id, changes) => {
    setConversations((p) => p.map((c) => (c.id === id ? { ...c, ...changes } : c)));
    setSelected((p) => (p && p.id === id ? { ...p, ...changes } : p));
  };

  const takeOver = async () => {
    if (!selected) return;
    try {
      await conversationApi.takeover(selected.id);
      patch(selected.id, { is_live_chat_active: true });
      toast.success("You are replying to this visitor");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const release = async () => {
    if (!selected) return;
    try {
      await conversationApi.release(selected.id);
      patch(selected.id, { is_live_chat_active: false });
      toast.success("Handed back to the assistant");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const send = async () => {
    if (!selected || !reply.trim()) return;
    setSending(true);
    try {
      const res = await conversationApi.reply(selected.id, reply);
      setMessages((p) => [...p, res.data]);
      setReply("");
    } catch (e) {
      toast.error(e.response?.status === 422
        ? "Message is too long. Please keep it under 8,000 characters."
        : formatApiError(e.response?.data?.detail));
    }
    finally { setSending(false); }
  };

  const setStatus = async (status) => {
    if (!selected) return;
    try {
      await conversationApi.update(selected.id, { status });
      patch(selected.id, { status });
      toast.success(status === "closed" ? "Marked as closed" : "Reopened");
    } catch { toast.error("Could not update"); }
  };

  const removeOne = async (id) => {
    if (!window.confirm("Delete this conversation and all of its messages? This cannot be undone.")) return;
    try {
      await conversationApi.delete(id);
      setConversations((p) => p.filter((c) => c.id !== id));
      if (selected?.id === id) { setSelected(null); setMessages([]); }
      toast.success("Conversation deleted");
    } catch { toast.error("Could not delete"); }
  };

  const exportOne = async (kind) => {
    if (!selected) return;
    const stamp = (who(selected) || "conversation").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    try {
      if (kind === "text") {
        const res = await conversationApi.exportText(selected.id);
        download(res.data, `${stamp}.txt`, "text/plain;charset=utf-8");
      } else if (kind === "json") {
        const res = await conversationApi.exportOneJson(selected.id);
        download(JSON.stringify(res.data, null, 2), `${stamp}.json`, "application/json");
      } else {
        /* There is no PDF endpoint on the server — the old page called one
           that never existed. The HTML export is real, so open that and let
           the browser print it to PDF. */
        window.open(conversationApi.exportHtmlUrl(selected.id), "_blank", "noopener");
        return;
      }
      toast.success("Exported");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
    }
  };

  const exportAll = async (kind) => {
    const day = new Date().toISOString().slice(0, 10);
    if (kind === "json") {
      try {
        const res = await conversationApi.exportAllJson(filterBot !== "all" ? filterBot : undefined);
        download(res.data, `conversations-${day}.json`);
        toast.success("Exported");
      } catch { toast.error("Export failed"); }
      return;
    }
    if (!conversations.length) { toast.error("Nothing to export"); return; }
    const esc = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const head = ["session_id", "assistant", "visitor_name", "visitor_email", "message_count", "status", "tags", "created_at"];
    const rows = conversations.map((c) => [
      c.session_id, botName(c.chatbot_id), c.visitor_name || "", c.visitor_email || "",
      c.message_count || 0, c.status || "open",
      Array.isArray(c.tags) ? c.tags.join("|") : "", c.created_at || "",
    ].map(esc).join(","));
    download([head.join(","), ...rows].join("\n"), `conversations-${day}.csv`, "text/csv;charset=utf-8");
    toast.success("Exported");
  };

  const bulkTag = async () => {
    if (!picked.length) return;
    const raw = window.prompt(`Add tags to ${picked.length} conversation(s), separated by commas:`, "");
    if (raw === null) return;
    const tags = raw.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 20);
    if (!tags.length) { toast.error("Enter at least one tag"); return; }
    try {
      await conversationApi.bulkTag(picked, tags, []);
      setConversations((p) => p.map((c) => picked.includes(c.id)
        ? { ...c, tags: Array.from(new Set([...(c.tags || []), ...tags])) } : c));
      toast.success("Tagged");
    } catch { toast.error("Could not tag"); }
  };

  const bulkDelete = async () => {
    if (!picked.length) return;
    if (!window.confirm(`Delete ${picked.length} conversation(s)? This cannot be undone.`)) return;
    try {
      await conversationApi.bulkDelete(picked);
      setConversations((p) => p.filter((c) => !picked.includes(c.id)));
      if (selected && picked.includes(selected.id)) { setSelected(null); setMessages([]); }
      setPicked([]); setPicking(false);
      toast.success("Deleted");
    } catch { toast.error("Could not delete"); }
  };

  const searchMessages = async () => {
    const q = msgQuery.trim();
    if (q.length < 2) return;
    try {
      const res = await workspaceApi.searchMessages(q, 25);
      setMsgResults(res?.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Search failed");
      setMsgResults([]);
    }
  };

  const botName = (id) => chatbots.find((b) => b.id === id)?.name || "Assistant";

  /* BC-020: what you may do depends on the role you hold on the chatbot.
     Delete is owner/workspace only. Replying and takeover are for workspace,
     agent, and co-owner; a viewer only reads and exports. */
  const botRole = (id) => chatbots.find((b) => b.id === id)?.access_role || "workspace";
  const canManage = (conv) => botRole(conv?.chatbot_id) === "workspace";
  const canReply = (conv) => ["workspace", "agent", "co_owner"].includes(botRole(conv?.chatbot_id));


  /* "Needs you" means exactly one thing: the assistant has stepped aside
     and a person is expected to answer. Nothing is guessed. */
  const needsYou = conversations.filter((c) => c.is_live_chat_active);
  const shown = tab === "needs" ? needsYou : conversations;
  const filtersOn = filterBot !== "all" || !!fromDate || !!toDate;

  const Tab = ({ id, label, count }) => (
    <button
      type="button" onClick={() => setTab(id)} data-testid={`tab-${id}`}
      className={`relative h-9 px-0.5 mr-7 text-[14px] transition-colors ${
        tab === id ? "text-b2-ink font-semibold" : "text-b2-muted hover:text-b2-ink font-medium"
      }`}
    >
      {label}{count > 0 && <span className="ml-1.5 text-b2-muted font-normal">{count}</span>}
      {tab === id && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-b2-indigo rounded-full" />}
    </button>
  );

  return (
    <div data-testid="conversations-page">
      <h1 className="text-[26px] leading-tight font-bold tracking-[-0.02em]">Conversations</h1>

      <div className="mt-5 border-b border-b2-line flex items-center">
        <Tab id="needs" label="Needs you" count={needsYou.length} />
        <Tab id="all" label="All conversations" count={0} />
      </div>

      <div className="mt-6 flex gap-5 items-start flex-col lg:flex-row">
        {/* ------------------------------- list ------------------------------- */}
        <div className="w-full lg:w-[320px] shrink-0 b2-card overflow-hidden">
          <div className="p-3 border-b border-b2-line flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-b2-faint" strokeWidth={1.8} />
              <input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search conversations"
                data-testid="conv-search"
                className="w-full h-9 pl-9 pr-3 rounded-[10px] bg-black/[0.03] border border-transparent text-[13.5px]
                           focus:bg-white focus:border-b2-indigo focus:outline-none transition"
              />
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button" data-testid="conv-filters"
                  title="Filters"
                  className={`w-9 h-9 rounded-[10px] flex items-center justify-center border transition-colors ${
                    filtersOn ? "border-b2-indigo text-b2-indigo bg-b2-wash" : "border-b2-line text-b2-muted hover:text-b2-ink"
                  }`}
                >
                  <SlidersHorizontal className="w-4 h-4" strokeWidth={1.8} />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 rounded-[14px] p-4 space-y-3">
                <div className="space-y-1.5">
                  <label className="text-[12.5px] font-medium text-b2-inksoft">Assistant</label>
                  <select
                    value={filterBot} onChange={(e) => setFilterBot(e.target.value)}
                    className="w-full h-9 px-2.5 rounded-[10px] border border-b2-line text-[13.5px] bg-white"
                  >
                    <option value="all">All assistants</option>
                    {chatbots.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <label className="text-[12.5px] font-medium text-b2-inksoft">From</label>
                    <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                           className="w-full h-9 px-2 rounded-[10px] border border-b2-line text-[13px] bg-white" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[12.5px] font-medium text-b2-inksoft">To</label>
                    <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                           className="w-full h-9 px-2 rounded-[10px] border border-b2-line text-[13px] bg-white" />
                  </div>
                </div>
                {filtersOn && (
                  <button
                    type="button"
                    onClick={() => { setFilterBot("all"); setFromDate(""); setToDate(""); }}
                    className="text-[13px] text-b2-indigo hover:underline"
                  >Clear filters</button>
                )}
              </PopoverContent>
            </Popover>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button" title="More" data-testid="conv-list-menu"
                  className="w-9 h-9 rounded-[10px] flex items-center justify-center border border-b2-line text-b2-muted hover:text-b2-ink"
                >
                  <MoreHorizontal className="w-4 h-4" strokeWidth={1.8} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => { setSearchOpen(true); setMsgResults(null); }}>
                  Search inside messages…
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { setPicking((v) => !v); setPicked([]); }}>
                  {picking ? "Done selecting" : "Select multiple…"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => exportAll("json")}>Export all as JSON</DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportAll("csv")}>Export this list as CSV</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {picking && (
            <div className="px-3 py-2 border-b border-b2-line bg-b2-wash flex items-center gap-2 text-[13px]">
              <span className="text-b2-washink font-medium">{picked.length} selected</span>
              <button onClick={bulkTag} disabled={!picked.length}
                      className="ml-auto text-b2-indigo hover:underline disabled:opacity-40">Tag</button>
              {picked.every((id) => canManage(conversations.find((c) => c.id === id))) && (
                <button onClick={bulkDelete} disabled={!picked.length}
                        className="text-red-600 hover:underline disabled:opacity-40">Delete</button>
              )}
            </div>
          )}

          <div className="max-h-[560px] overflow-y-auto">
            {loading ? (
              <div className="py-16 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-b2-faint" /></div>
            ) : shown.length === 0 ? (
              <p className="px-5 py-14 text-center text-[13.5px] text-b2-muted">
                {tab === "needs"
                  ? "Nothing waiting on you. Conversations appear here when you take one over from an assistant."
                  : search || filtersOn
                    ? "No conversations match that."
                    : "No conversations yet."}
              </p>
            ) : (
              <ul className="divide-y divide-b2-line">
                {shown.map((c) => {
                  const active = selected?.id === c.id;
                  return (
                    <li key={c.id}>
                      <div
                        role="button" tabIndex={0}
                        onClick={() => (picking
                          ? setPicked((p) => p.includes(c.id) ? p.filter((x) => x !== c.id) : [...p, c.id])
                          : open(c))}
                        onKeyDown={(e) => { if (e.key === "Enter") open(c); }}
                        data-testid="conv-row"
                        className={`w-full text-left flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                          active ? "bg-b2-wash" : "hover:bg-black/[0.02]"
                        }`}
                      >
                        {picking && (
                          <span className={`w-[18px] h-[18px] rounded-[5px] border flex items-center justify-center shrink-0 ${
                            picked.includes(c.id) ? "bg-b2-indigo border-b2-indigo text-white" : "border-b2-linestrong"
                          }`}>
                            {picked.includes(c.id) && <Check className="w-3 h-3" strokeWidth={3} />}
                          </span>
                        )}
                        <span className="w-8 h-8 rounded-full bg-black/[0.05] text-b2-inksoft flex items-center justify-center text-[12px] font-semibold shrink-0">
                          {who(c).charAt(0).toUpperCase()}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="flex items-center gap-1.5">
                            <span className={`text-[14px] truncate ${active ? "font-semibold text-b2-washink" : "font-medium text-b2-ink"}`}>
                              {who(c)}
                            </span>
                            {c.is_live_chat_active && (
                              <span className="w-1.5 h-1.5 rounded-full bg-b2-live shrink-0" title="You are replying" />
                            )}
                          </span>
                          <span className="block text-[12.5px] text-b2-muted truncate">{botName(c.chatbot_id)}</span>
                        </span>
                        <span className="text-[12px] text-b2-faint shrink-0">{shortDate(c.created_at)}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {tab === "all" && hasMore && !loading && (
              <button
                onClick={loadMore} disabled={loadingMore}
                className="w-full py-3 text-[13px] text-b2-indigo hover:underline border-t border-b2-line"
              >{loadingMore ? "Loading…" : "Load more"}</button>
            )}
          </div>
        </div>

        {/* ------------------------------ detail ------------------------------ */}
        <div className="flex-1 min-w-0 w-full b2-card flex flex-col min-h-[560px]">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8 py-20">
              <MessageSquare className="w-8 h-8 text-b2-faint" strokeWidth={1.5} />
              <p className="mt-4 text-[16px] font-semibold">Pick a conversation</p>
              <p className="mt-1.5 text-[13.5px] text-b2-muted">Its messages will open here.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 px-6 py-4 border-b border-b2-line">
                <span className="w-9 h-9 rounded-full bg-black/[0.05] text-b2-inksoft flex items-center justify-center text-[13px] font-semibold shrink-0">
                  {who(selected).charAt(0).toUpperCase()}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-semibold truncate">{who(selected)}</p>
                  <p className="text-[12.5px] text-b2-muted truncate">
                    {botName(selected.chatbot_id)} · {shortDate(selected.created_at)}
                    {selected.status === "closed" && " · Closed"}
                  </p>
                </div>
                {selected.is_live_chat_active && (
                  <span className="hidden sm:inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-b2-livewash text-b2-live text-[12px] font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-current" /> You are replying
                  </span>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="conv-menu"
                            className="w-9 h-9 rounded-[10px] flex items-center justify-center text-b2-muted hover:text-b2-ink hover:bg-black/[0.04]">
                      <MoreHorizontal className="w-[18px] h-[18px]" strokeWidth={1.8} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {canReply(selected) && (selected.is_live_chat_active
                      ? <DropdownMenuItem onClick={release}>Hand back to the assistant</DropdownMenuItem>
                      : <DropdownMenuItem onClick={takeOver}>Take over and reply</DropdownMenuItem>)}
                    {canReply(selected) && (
                      <DropdownMenuItem onClick={() => setStatus(selected.status === "closed" ? "open" : "closed")}>
                        {selected.status === "closed" ? "Reopen conversation" : "Mark as closed"}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => exportOne("text")}>Export as text</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => exportOne("json")}>Export as JSON</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => exportOne("html")}>Open printable version</DropdownMenuItem>
                    {canManage(selected) && (
                      <DropdownMenuSeparator />
                    )}
                    {canManage(selected) && (
                      <DropdownMenuItem onClick={() => removeOne(selected.id)} className="text-red-600 focus:text-red-600">
                        Delete conversation
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 max-h-[420px]">
                {loadingMessages ? (
                  <div className="py-14 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-b2-faint" /></div>
                ) : messages.length === 0 ? (
                  <p className="py-14 text-center text-[13.5px] text-b2-muted">No messages in this conversation.</p>
                ) : messages.map((m, i) => {
                  const mine = m.role === "assistant" || m.role === "agent";
                  const label = m.role === "agent" ? (m.agent_name || "You") : m.role === "user" ? who(selected) : botName(selected.chatbot_id);
                  return (
                    <div key={m.id || i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[78%] ${mine ? "items-end" : "items-start"} flex flex-col gap-1`}>
                        <span className="text-[11.5px] text-b2-faint px-1">{label}</span>
                        <div className={`px-3.5 py-2.5 rounded-[14px] text-[14px] leading-relaxed ${
                          m.role === "agent" ? "bg-b2-indigo text-white"
                            : mine ? "bg-b2-wash text-b2-ink"
                            : "bg-black/[0.035] text-b2-ink"
                        }`}>
                          <ReactMarkdown components={MD}>{m.content || ""}</ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              <div className="border-t border-b2-line px-6 py-4">
                {!canReply(selected) ? (
                  <p className="text-[13px] text-b2-muted">
                    You have Viewer access to this assistant, so you can read and export but not reply.
                  </p>
                ) : selected.is_live_chat_active ? (
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <textarea
                        rows={1} value={reply} onChange={(e) => setReply(e.target.value)}
                        maxLength={8000}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        placeholder="Write a reply…" data-testid="conv-reply"
                        className="w-full resize-none px-3.5 py-2.5 rounded-[10px] border border-b2-linestrong text-[14px]
                                   focus:outline-none focus:border-b2-indigo focus:ring-2 focus:ring-b2-indigo/15 transition"
                      />
                      {reply.length > 7500 && (
                        <p className="text-xs text-muted-foreground mt-1 text-right" data-testid="conv-reply-count">
                          {reply.length.toLocaleString()} / 8,000
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={send} disabled={sending || !reply.trim()} data-testid="conv-send"
                      className="h-10 px-4 rounded-[10px] bg-b2-indigo hover:bg-b2-indigohover text-white font-semibold shadow-none"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={2} />}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="text-[13px] text-b2-muted flex-1 min-w-[180px]">
                      The assistant is handling this conversation.
                    </p>
                    <button onClick={takeOver} data-testid="conv-takeover"
                            className="text-[13.5px] font-medium text-b2-indigo hover:underline">
                      Take over and reply
                    </button>
                  </div>
                )}
                <button
                  onClick={() => exportOne("text")} data-testid="conv-export"
                  className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] text-b2-muted hover:text-b2-ink"
                >
                  <Download className="w-3.5 h-3.5" strokeWidth={1.8} /> Export conversation
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="rounded-[14px] max-w-lg">
          <DialogHeader><DialogTitle>Search inside messages</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <input
              autoFocus value={msgQuery} onChange={(e) => setMsgQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") searchMessages(); }}
              placeholder="What was said?" data-testid="msg-search-input"
              className="flex-1 h-10 px-3.5 rounded-[10px] border border-b2-linestrong text-[14px]
                         focus:outline-none focus:border-b2-indigo"
            />
            <Button
              onClick={searchMessages} disabled={msgQuery.trim().length < 2}
              className="h-10 px-4 rounded-[10px] bg-b2-indigo hover:bg-b2-indigohover text-white font-semibold shadow-none"
            >Search</Button>
          </div>
          {msgResults !== null && (
            <div className="mt-2 max-h-[320px] overflow-y-auto">
              {msgResults.length === 0 ? (
                <p className="py-6 text-center text-[13.5px] text-b2-muted">Nothing matched.</p>
              ) : (
                <ul className="divide-y divide-b2-line">
                  {msgResults.map((r, i) => (
                    <li key={r.id || i} className="py-2.5">
                      <button
                        className="w-full text-left"
                        onClick={() => {
                          const conv = conversations.find((c) => c.id === r.conversation_id);
                          if (conv) { open(conv); setSearchOpen(false); }
                          else toast.error("That conversation is not in the current list");
                        }}
                      >
                        <p className="text-[13.5px] text-b2-ink line-clamp-2">{r.content}</p>
                        <p className="text-[12px] text-b2-faint mt-0.5">
                          {r.role === "user" ? "Visitor" : "Assistant"}
                          {r.created_at ? ` · ${shortDate(r.created_at)}` : ""}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
